import { LLMResponse, RiskReportDisplay } from "../types";
import api from "./api";

class LLMService {
    private baseUrl = "/quoter";

    private listeners: ((riskAnalysis: LLMResponse) => void)[] = [];

    subscribe = (listener: (data: LLMResponse) => void) => {
        this.listeners.push(listener);
        return () => {
            this.listeners = this.listeners.filter((l) => l !== listener);
        };
    }

    notifySubscribers(riskAnalysis: LLMResponse) {
        this.listeners.forEach((listener) => listener(riskAnalysis));
    }

    getLastRiskAnalysis = async (): Promise<LLMResponse> => {
        try {
            const { data } = await api.get(this.baseUrl + "/last-risk-analysis");
            return data as LLMResponse;
        } catch (error) {
            console.error(error);
            return null;
        }
    }

    analyseRisk = async (question: string, message: string): Promise<[boolean, LLMResponse]> => {
        try {
            const { data } = await api.post(this.baseUrl + "/llm-analyse-risk", {question, input: message});
            this.notifySubscribers(data.llmResponse);
            return [data.valid, data.llmResponse];
        } catch (error) {
            console.error(error);
            return null;
        }
    }

    analyseFlags = async (): Promise<LLMResponse> => {
        try {
            const { data } = await api.get(this.baseUrl + "/llm-analyse-flags");
            return data.llmResponse as LLMResponse;
        } catch (error) {
            console.error(error);
            return null;
        }
    }

    analyseConversation = async (question: string, answer: string): Promise<ConversationResponse> => {
        try {
            const { data } = await api.post(this.baseUrl + "/llm-analyse-conversation", {question, answer});
            this.notifySubscribers(data.llmResponse);
            return data as ConversationResponse;
        } catch (error) {
            console.error(error);
            return null;
        }
    }

    getInitialQuestion = async (): Promise<ConversationResponse> => {
        try {
            const { data } = await api.post(this.baseUrl + "/llm-initial-question");
            this.notifySubscribers(data.llmResponse);
            return data as ConversationResponse;
        } catch (error) {
            console.error(error);
            return null;
        }
    }

    postFlagQuestionAnswers = async (answers: string): Promise<boolean> => {
        try {
            const { data } = await api.post(this.baseUrl + "/llm-flag-questions", { answers });
            return true;
        } catch (error) {
            console.error(error);
            return false;
        }
    }

    getWebsiteCheck = async (websiteUrl: string): Promise<boolean> => {
        try {
            const _ = await api.get(this.baseUrl + "/website-check", { params: { url: websiteUrl } });
            return true;
        } catch (error) {
            console.error(error);
            return false;
        }
    }

    getWebsiteCheckResult = async (): Promise<{result: string}> => {
        try {
            const { data } = await api.get(this.baseUrl + "/website-check-result");
            return data;
        } catch (error) {
            console.error(error);
            return null;
        }
    }

    answerConsistencyQuestion = async (answer: string): Promise<ConsistencyQuestionResponse> => {
        try {
            const { data } = await api.post(this.baseUrl + "/llm-consistency-question", {answer});
            return data as ConsistencyQuestionResponse;
        } catch (error) {
            console.error(error);
            return null;
        }
    }

    analyseConsistency = async (): Promise<LLMResponse> => {
        try {
            const { data } = await api.get(this.baseUrl + "/llm-analyse-consistency");
            return data.llmResponse;
        } catch (error) {
            console.error(error);
            return null;
        }
    }

    getLastConsistencyAnalysis = async (): Promise<LLMResponse> => {
        try {
            const { data } = await api.get(this.baseUrl + "/last-consistency-analysis");
            return data.llmResponse;
        } catch (error) {
            console.error(error);
            return null;
        }
    }

    getRiskReport = async (): Promise<RiskReportDisplay> => {
        try {
            const { data } = await api.get(this.baseUrl + "/risk-report");
            return data.riskReport as RiskReportDisplay;
        } catch (error) {
            console.error(error);
            return null;
        }
    }
}

export default new LLMService();

interface ConversationResponse {
    llmResponse: LLMResponse;
    valid: boolean;
    finished: boolean;
}

interface ConsistencyQuestionResponse {
    llmResponse: LLMResponse;
    valid: boolean;
}
